/**
 * Content for the /how-to guide collection: the intro letter and chapters.
 * Chapters marked "soon" render in the rail but are not yet navigable.
 */
export interface GuideChapter {
  slug: string;
  numeral: string;
  title: string;
  status: "live" | "soon";
  /** Kicker note shown opposite the chapter label. */
  note?: string;
  headline?: string;
  sections?: {
    id: string;
    title: string;
    paras: string[];
    shot?: {
      src: string;
      width: number;
      height: number;
      alt: string;
      caption: string;
    };
    callout?: string;
    aside?: boolean;
    links?: {
      label: string;
      href: string;
    }[];
  }[];
}

export const GUIDE_TITLE = "How to run a credit repair business";

export const INTRO_LETTER = {
  headline: "How to run a credit repair business, from people who ran them.",
  paras: [
    "Before DisputeWell existed, we ran credit repair businesses ourselves. We learned this industry the way most people do, which is the hard way. Figuring out licensing state by state, learning what actually gets deletions by losing months to what doesn't, and paying for software that still made us do most of the work.",
    "This guide is everything we wish someone had handed us at the start. How to set the business up legally. How disputing actually works once your letter lands at a bureau. How to get clients without becoming a full-time marketer. And how to scale past the point where your own hours are the ceiling.",
    "None of it is locked behind a course, and none of it requires our software. We teach it straight, and where DisputeWell happens to do a step for you, we'll show you that too. We built the platform because we couldn't buy it. We wrote this guide because nobody handed it to us.",
    "Read it in order, or jump to whatever you're stuck on. Either way, you'll come out knowing more about this business than most of the people selling you things.",
  ],
  signature: "Kane and Dylan · Founders, DisputeWell",
} as const;

export const GUIDE_CHAPTERS: GuideChapter[] = [
  {
    slug: "start",
    numeral: "I",
    title: "Start",
    status: "live",
    note: "The setup, in order",
    headline: "How to start a credit repair business.",
    sections: [
      {
        id: "introduction",
        title: "Introduction",
        paras: [
          "Starting a credit repair business does not take an office, employees, or a certification course. It takes a legal setup done right, a way to read credit reports, a way to send disputes, and one client you can actually help. This chapter is the setup, in order.",
        ],
      },
      {
        id: "how-i-started",
        title: "How I got started",
        aside: true,
        paras: [
          "Before we start, I wanted to share a quick story about how I got started. I started my credit repair business in 2020, and it was a long shot. I never had much information on how to start a business, but I did find a course that cost $1,000 to learn how to dispute inaccurate info off of credit reports. I studied it religiously and found great information and real success with it, but I still had to establish myself as the credit guy and learn how to build a real business.",
          "I started with a network of friends and family. Eventually, after trial and error, I learned that I could actually remove negative items from other people's reports, and it started to build momentum. But there was a lot of marketing that had to go into it.",
          "As the business grew, I used both of the major disputing platforms, DisputeFox and Credit Repair Cloud, and I kept wondering to myself why it felt like they didn't actually care about my business. They didn't want to help me become compliant. They actually made it harder. And they didn't care if I scaled the business, only if I paid my monthly subscription.",
          "They barely showed the metrics for success either. Is this methodology working? Are my disputes effective? Is my client engaged? Everything I feel is extremely important from a business perspective, and I couldn't see any of it.",
          "That's how DisputeWell started, and that's the story. The rest of this guide is everything I had to figure out along the way.",
        ],
      },
      {
        id: "is-this-for-you",
        title: "Is this business for you?",
        paras: [
          "Honest picture first. This is a service business with recurring revenue, which is the good part. Clients pay monthly, results take rounds, and your reputation compounds. The clients you are serving are people who have a hard time paying their bills, and that can be you too.",
          "You are also contributing a large portion to their financial wellness. There will be many people who are able to buy their first home because of your hard work and effort. Trust is everything and shortcuts can get you sued. If you want a passive income hack, this isn't it. If you want a real business helping people with a problem that genuinely ruins lives, keep reading.",
        ],
      },
      {
        id: "federal-floor",
        title: "Federal Law: CROA",
        paras: [
          "Start with the federal law, because it applies to everyone: the Credit Repair Organizations Act, CROA. Congress wrote it specifically about businesses like yours, and it's readable, but not always easily understood.",
          "You need a written contract with specific disclosures, including a notice of your client's rights. Every client gets three business days to cancel after signing. You can't charge for work before it's performed, which is why this industry bills monthly, after the work. And you can never advise anyone to lie, to dispute accurate information as inaccurate, or to build a new credit identity to escape their file.",
          "None of this is optional, it is the law and it will affect you. The largest company in this industry's history was hit with a $2.7 billion judgment, but if you follow the law from day one, none of that will ever be your problem. We are the only major platform that actually includes tools for you to monitor your compliance, and whether each client you're working for is compliant too.",
          "Now, there is real nuance in what you can and cannot do. A quick taste: you can dispute anything inaccurate, unverifiable, or incomplete, but you can never guarantee a deletion or promise a score, no matter how confident you are. That line is where businesses get in trouble, and DisputeWell bakes the guardrails in. We'll go deeper on the do's and don'ts as the guide grows. For now, your state's rules.",
        ],
        links: [
          {
            label: "The full text of CROA, 15 U.S.C. §1679",
            href: "https://www.law.cornell.edu/uscode/text/15/chapter-41/subchapter-II-A",
          },
          {
            label: "The FTC's CROA page",
            href: "https://www.ftc.gov/legal-library/browse/statutes/credit-repair-organizations-act",
          },
        ],
      },
      {
        id: "state-rules",
        title: "Your state's rules",
        paras: [
          "Next are the state-specific rules, still just as important as your federal law. Every state genuinely is different. We track all 53 US jurisdictions inside DisputeWell, so the system knows if you need a certain agreement, bond, or license.",
          "36 states require a surety bond. A bond isn't money you park somewhere: you pay a small annual premium, usually a percent or two of the bond amount, and a surety company guarantees the rest. The amounts run from $5,000 in the lightest states up to $100,000, and Kansas can technically require up to $1,000,000. 25 states want you registered with a state agency before you operate, and seven go further and require a dedicated credit repair license: Idaho, Kansas, Maryland, Mississippi, Oklahoma, South Carolina, and Puerto Rico.",
          "Contracts get state verbiage too. 34 states set their own cancellation window, anywhere from 3 to 10 days, and eleven cap how long a contract can even run. Ohio caps it at 60 days, Michigan at 90, most of the rest at six months, and South Carolina lets you go five years. If your contract doesn't match your state's rules, it may not be enforceable at all. Which obviously is bad from a legal perspective. We set reminders for when contracts need to be renewed and things as well.",
          "This is the part of starting up where people stall for months. Don't. Look up your state, get the bond quote, file the registration if you need one, and move. And if your state's rules read strict or strange, spend the few hundred dollars on a lawyer consult before you spend anything else.",
        ],
        shot: {
          src: "/graphics/dw-shot-compliance.png",
          width: 1920,
          height: 1080,
          alt: "The 50-state compliance dashboard: a US map shaded by readiness with per-state bond, license, and registration tracking",
          caption:
            "The 50-state map. If it's green you're good. if it's yellow or red you need to do some more paperwork.",
        },
        callout:
          "The compliance dashboard tracks bond, license, and registration requirements for all 53 US jurisdictions, computes your state's cancellation windows into every contract, and blocks intake in states where you're not ready.",
      },
      {
        id: "forming-the-company",
        title: "Forming the company",
        paras: [
          "Forming the company is simpler than it sounds, and you can do the whole thing in an afternoon. Pick a business name, file an LLC on your state's Secretary of State website (most states charge somewhere between $50 and a few hundred dollars), and grab your EIN from the IRS website. The EIN is free and takes about ten minutes, and it's what lets your business exist on paper. The bank account, the bond, and your state registration all require that number too.",
          "Then open a business bank account and run every dollar of the business through it. Client money and personal money should never touch. You're a regulated business handling other people's financial lives, and if anyone ever asks questions, clean books from day one will set you up for success.",
          "Here's another significant thing to mention about this industry, banks and payment processors treat credit repair as a high-risk. Some of the large processors simply will not serve you. That's not a reflection of your business, it's their policy, so plan around it from the start. Use a payment gateway that accepts credit repair, and be upfront about what your business does when you open accounts, because getting shut down mid-operation hurts a lot more than being told no on day one.",
          "Because of that high-risk label, getting a bank and merchant account can be more tedious than it needs to be. That's a problem with the industry's reputation, not with you. And it's part of why we built DisputeWell the way we did: our hope is that through data, documented outcomes, and provable compliance, credit repair stops being a high-risk industry at all.",
          "You don't need an office, you can be your own registered agent, and you don't need some special out-of-state corporation. Your own state, a simple LLC, a real bank account, and a processor that accepts your industry. That's the whole checklist.",
        ],
        callout:
          "DisputeWell connects client billing to Authorize.net and NMI, with recurring plans, portal payments, and tokenized card storage built in.",
      },
      {
        id: "toolkit",
        title: "Your toolkit",
        paras: [
          "You need three things. A credit monitoring service your clients sign up for, so you can pull their reports every month. Software to read those reports, find what's disputable, and write the letters. And a way to print and mail everything with tracking, because every dispute in this industry travels by mail. (you can fax too, but that's not the best way to do it)",
        ],
        shot: {
          src: "/graphics/dw-shot-dispute-rounds.png",
          width: 3164,
          height: 1772,
          alt: "A client's Disputes tab in dark mode with Round 6 selected, bureau score changes, tradeline rows, and the Fluid assistant open beside the round timeline",
          caption: "Round timeline and Fluid assistant on the DisputeWell Disputes tab.",
        },
        callout:
          "Monitoring imports, analysis, letters, and tracked mail are all one system in DisputeWell.",
      },
      {
        id: "first-client",
        title: "Your first client",
        paras: [
          "Start with someone whose situation you know, a friend or family member with real negatives on their report. Pull their reports inside of Disputewell with our one click import, let your agent fluid find the best disputable items, and generate your reasons and instructions based off of the full report and situation context. ",
          "Fluid is trained on thousands of successful disputes and will generate the strongest reasons and instructions for you. You are a credit repair professional, and your disputes deserve to be top of the line. You'll learn more from one real round than from a month of videos. And that first deletion letter is the moment people will start to realize this is the real deal.",
        ],
      },
      {
        id: "pricing",
        title: "Pricing your service",
        paras: [
          "Most of this industry charges monthly, and this is where some of the CROA nuance comes into play. You charge after work is performed, some people consider that the consultation and analysis, others say it's when the score increases, or items are deleted. ",
          "This is where the portal becomes important. Your clients log in under your brand and see the accounts that have been disputed, letters sent, score changes, what got sent, what came back, etc. They can review and approve their upcoming disputes, watch their score history, upload documents when you need them, message you, and pay their invoice, all in one place. Every one of those touches is your monthly price defending itself.",
          "Clients more often than not would've just messaged me directly instead of using the portal in other softwares. We want our portal to be a real benefit to your company, so it is more interactive and engaging than the other softwares, which by proxy helps make you more compliant as well.",
        ],
        shot: {
          src: "/graphics/dw-shot-portal-rounds.png",
          width: 1920,
          height: 1080,
          alt: "The client portal under a business's own brand: a client on round five reviewing his disputed items with an Approve Round button",
          caption:
            "The portal, under your brand. This client is on round five, reading the strategy and approving the round himself.",
        },
        callout:
          "The client portal shows every round in plain language, and billing runs itself after each cycle.",
      },
      {
        id: "take-the-leap",
        title: "Take the leap",
        paras: [
          "Here's what week one actually looks like. Form the LLC and grab your EIN. Look up your state and start the bond or registration if you need one. Get your CROA contract in place. Sign up for a monitoring service, pick your software, and enroll your first client, even if that first client is your cousin.",
          "None of those steps are hard. What's hard is that nobody is going to make you do them. There's no boss, no deadline, and no perfect moment coming. Most people who want to start this business stay stuck at wanting: reading one more thing, watching one more video, waiting until they feel ready.",
          "When I started in 2020, I had a $1,000 course and a list of friends and family. You have the entire legal map, the actual mechanics of disputing, and software that does most of the work for you. You're starting with more than almost anyone who has ever built one of these businesses.",
          "So take the leap. Enroll one person this week. And when their reports come in and you're staring at three bureaus' worth of accounts wondering what to dispute, that's Chapter II.",
        ],
      },
    ],
  },
  {
    slug: "dispute",
    numeral: "II",
    title: "Dispute",
    status: "live",
    note: "The part nobody explains",
    headline: "How disputing actually works.",
    sections: [
      {
        id: "introduction",
        title: "Introduction",
        paras: [
          "Most people in this industry send letters without knowing what happens on the other end. That's why most letters fail. This chapter walks through the pipeline your dispute actually travels, what survives it, and how rounds are supposed to build on each other.",
        ],
      },
      {
        id: "what-happens",
        title: "What happens to your letter",
        paras: [
          "When your dispute arrives at a bureau, a person reads it with about four minutes to spend. They don't study your argument. They boil the whole letter down to one of 29 dispute codes, and an automated form carries that code to the company reporting the debt. That company's default move is to check the code against its own records and answer verified. Nobody re-reads what you wrote.",
          "Regulators' studies found that roughly 90% of disputes collapse into the same four generic codes. That's the pipeline. Long emotional letters, template walls, and clever wording all get crushed into the same code as everyone else's mail.",
        ],
      },
      {
        id: "what-works",
        title: "What actually gets deletions",
        paras: [
          "A letter survives that pipeline when it makes one specific claim the data itself backs up. Credit reporting runs on a rulebook called Metro 2, the format every creditor uses to report your client's accounts. When the reported data breaks those rules, or the three bureaus contradict each other, you have a claim that doesn't depend on anyone's sympathy. The reviewer can see it in their own records.",
          "That's the whole philosophy. Don't argue, point. One item, one field, one contradiction, backed by the law that requires accuracy.",
        ],
        shot: {
          src: "/graphics/dw-shot-insights.png",
          width: 1920,
          height: 1080,
          alt: "The causality graph: a traced chain from dispute reason through account, bureau, and round to outcome and score change",
          caption:
            "The chain from claim to outcome. Every dispute leaves a trace you can read back later.",
        },
        callout:
          "DisputeWell checks every account against 48 compliance rules from the 2023 CRRG, the manual the bureaus themselves report by, and turns each violation into a typed, citable claim.",
      },
      {
        id: "rounds",
        title: "Rounds and escalation",
        paras: [
          "After you mail, the FCRA gives bureaus about 30 days to investigate and respond. What comes back decides your next move, and this is where most of the industry gets lazy. Rewording round one and sending it again is not a round two.",
          "A real round two answers what happened. If they said verified without showing how, the next letter demands the method. If they went silent past the deadline, the next letter demands deletion, because that's the law's own remedy. If a deleted item sneaks back on, there are specific rules about reinsertion and your letter should cite them. Every response has a correct next move.",
        ],
        shot: {
          src: "/graphics/dw-shot-fluid-schedule.png",
          width: 3024,
          height: 1646,
          alt: "Asking Fluid to schedule disputes for a client: it loads the record and confirms the round, ready for automation",
          caption:
            "Round five confirmed, round six on the calendar. The clock runs itself.",
        },
        callout:
          "Dispute tracks the clock per round and builds the next letter from what actually happened: the response, the deadline, and the account's full history.",
      },
      {
        id: "when-not",
        title: "When not to dispute",
        paras: [
          "Here's the part that keeps you in business for the long run: some accounts give you nothing to work with, and the right move is to not dispute them. The dispute-everything model made a lot of money for a while, and the biggest company that ran it ended with a $2.7 billion judgment.",
          "No grounds, no dispute. Your clients get honesty, the bureaus get letters they can't dismiss, and you get a business that survives an audit.",
        ],
      },
      {
        id: "results",
        title: "Reading the results",
        paras: [
          "Every round ends with a new report, and the new report is the scoreboard. Compare it against the last one, per bureau: what got deleted, what changed, what got verified, and what never got answered. Deletions get credited to the round that earned them, which is how you learn what actually works for your book.",
        ],
        callout:
          "Every imported report is compared against the last one automatically. Deletions, changes, and reinsertions get caught and logged per bureau, and outcomes feed the next round's strategy.",
      },
    ],
  },
  {
    slug: "clients",
    numeral: "III",
    title: "Get clients",
    status: "live",
    note: "Without becoming a marketer",
    headline: "How to get clients.",
    sections: [
      {
        id: "introduction",
        title: "Introduction",
        paras: [
          "You don't need to run ads to fill a credit repair business. Most of the durable businesses in this industry are built on referrals and a smooth way in. This chapter is about building both.",
        ],
      },
      {
        id: "first-ten",
        title: "Your first ten",
        paras: [
          "Your first clients are closer than you think. Friends, family, coworkers, and their friends. Almost everyone knows someone with credit problems, and the person who fixes them talks about it. Do serious work for a small circle and let the results introduce you.",
          "The other early channel is people who see credit denials all day: loan officers, realtors, car dealers. When they have to turn someone away, they'd rather hand them a path back than lose the deal forever. One or two of those relationships can feed a whole business.",
        ],
      },
      {
        id: "the-way-in",
        title: "The way in",
        paras: [
          "When someone's ready, the distance between interested and enrolled decides whether you get them. The way in should be one link: they read about your service, sign the agreements, upload their ID, and connect their credit monitoring in one sitting, from their phone.",
        ],
        shot: {
          src: "/graphics/dw-shot-onboarding-framed.png",
          width: 1920,
          height: 1080,
          alt: "A branded onboarding flow: agreements, e-signature, ID upload, and monitoring credentials from one link",
          caption:
            "The way in: one link, one sitting, from their phone. Under your brand the whole way.",
        },
        callout:
          "Public intake pages capture leads, and onboarding runs the whole path from a single link: agreements with e-sign, ID upload, and monitoring credentials.",
      },
      {
        id: "keeping-them",
        title: "Keeping them",
        paras: [
          "Getting clients is half the job. Keeping them through a process that takes months is the other half, and it's won with communication, not discounts. A client who can see what's happening doesn't cancel, doesn't call for status, and doesn't charge back.",
          "Send updates when things happen. A deletion is a text worth sending the same day.",
        ],
        shot: {
          src: "/graphics/dw-shot-portal.webp",
          width: 2000,
          height: 1091,
          alt: "The client portal: score gauge, dispute progress in plain language, and letter tracking",
          caption:
            "What your clients see. A client who can watch the work happening doesn't call for status.",
        },
        callout:
          "The portal shows every dispute in plain language, and drip campaigns send score alerts and round updates from your own domain automatically.",
      },
      {
        id: "referrals",
        title: "Referrals compound",
        paras: [
          "Ask at the win moments. The day a deletion lands, your client is proud of the decision they made and happy to tell someone. That's when asking if they know anyone else dealing with this actually works. A business that wins deletions and asks at the right moments grows on its own.",
        ],
      },
    ],
  },
  {
    slug: "scale",
    numeral: "IV",
    title: "Scale",
    status: "live",
    note: "The ceiling is optional",
    headline: "How to scale past your own hours.",
    sections: [
      {
        id: "introduction",
        title: "Introduction",
        paras: [
          "In most credit repair businesses, every new client costs the owner hours, so the business quietly caps itself at whatever you can personally handle. Scaling means removing yourself from every step that repeats.",
        ],
      },
      {
        id: "automate",
        title: "Automate the cycle",
        paras: [
          "The dispute cycle is the same loop for every client, every month: pull the report, read it, pick items, write letters, mail them, wait, read the results, go again. Every step of that loop except one can run without you.",
        ],
        callout:
          "Enroll a client once and rounds prep themselves, on a schedule or the moment a fresh report lands. Fluid picks the items, writes the letters, and queues everything behind your approval.",
      },
      {
        id: "approvals",
        title: "Approve, don't do",
        paras: [
          "The step you keep is judgment. Your day shifts from doing tasks to reviewing decisions: read what was prepared, approve what's right, correct what isn't. Ten minutes of approvals can move fifty clients. That's the difference between running a caseload and running a business.",
        ],
      },
      {
        id: "hiring",
        title: "Hiring, later than you think",
        paras: [
          "Software should push your first hire out much further than the old model did. And when you do hire, hire for conversations, not dispute expertise, because the expertise lives in the system. Give people the access their job needs and nothing more, and keep charging authority locked down.",
        ],
        callout:
          "Roles gate what each person can see and do, sensitive actions land in an audit trail, and by default agents can't charge cards at all.",
      },
      {
        id: "numbers",
        title: "Watch the numbers",
        paras: [
          "Scale changes what you look at. Client count and revenue tell you where you are. Deletions in flight and score gains tell you where you're going. And once you know which dispute strategies actually earn deletions for your book, you stop guessing what to send.",
        ],
        shot: {
          src: "/graphics/dw-shot-dashboard.png",
          width: 1920,
          height: 1080,
          alt: "The main dashboard: a morning briefing of what happened yesterday and what needs you today, headline stats, and the growth chart",
          caption:
            "The morning briefing: what happened yesterday, what needs you today, and the numbers underneath.",
        },
        callout:
          "The dashboard tracks the business numbers live, and outcome attribution ties every deletion back to the round and strategy that earned it.",
      },
    ],
  },
];

export function getGuideChapter(slug: string): GuideChapter | undefined {
  return GUIDE_CHAPTERS.find(
    (chapter) => chapter.slug === slug && chapter.status === "live",
  );
}
